import { createTRPCUntypedClient, httpLink } from "@trpc/client";
import { JourneyError } from "../contracts";
import type { DeliveryEstimate } from "../contracts";
import {
  attemptReadOf,
  StartRefused,
  startRefusalOf,
  watchAttempt,
  type AttemptChanges,
  type PurchaseStart,
  type PurchaseTransport,
} from "./transport";

/** A caller error is the server's answer: its code (tRPC's message), and a
 *  refused start's words (its `data.refusal`, LIS-1173); anything else —
 *  the network, a server failure — may not be. */
function answered<T>(request: Promise<T>): Promise<T> {
  return request.catch((error: unknown) => {
    const data = (error as { data?: { httpStatus?: unknown; refusal?: unknown } } | null)?.data;
    const status = data?.httpStatus;
    const code = (error as { message?: unknown } | null)?.message;
    if (typeof status !== "number" || status < 400 || status >= 500 || typeof code !== "string") throw error;
    const refusal = startRefusalOf(data?.refusal);
    throw Object.assign(refusal ? new StartRefused(code, refusal) : new JourneyError(code), { status });
  });
}

/**
 * The purchase sheet over the api's buy-on-behalf tRPC router (the Lissen
 * app, natively and on app-web). The host gives the signed-in read of the
 * purchase (`read`: the app's access read, `accesses.getById`) and where a
 * start comes from (`origin`, the start's funnel is the host's to add).
 * Every attempt read is held to its schema.
 */
export function purchaseRpcTransport(options: {
  url: string;
  headers: () => Promise<Record<string, string>>;
  read: PurchaseTransport["read"];
  /** A start's own fields the host adds: where it comes from, its funnel. */
  start: (input: PurchaseStart) => Record<string, unknown>;
  /** The fetch tRPC sends with; the platform's own by default. */
  fetch?: typeof fetch;
  /** What says an attempt changed (the app's realtime socket): a watched
   *  attempt is read at once on each change, and polled as the fallback. */
  changes?: AttemptChanges;
}): PurchaseTransport {
  const api = createTRPCUntypedClient({
    links: [httpLink({ url: options.url, headers: options.headers, ...(options.fetch ? { fetch: options.fetch } : {}) })],
  });
  const query = <T>(path: string, input?: unknown) => answered(api.query(path, input) as Promise<T>);
  const mutate = <T>(path: string, input?: unknown) => answered(api.mutation(path, input) as Promise<T>);
  return {
    read: options.read,
    identity: () => query("identity"),
    beginCardCapture: (cardId) => mutate("beginCardCapture", { cardId }),
    completeCardCapture: (input) => mutate("completeCardCapture", input),
    estimateDelivery: (input) => mutate<DeliveryEstimate>("estimateDelivery", input),
    deliveryEstimate: (sheetId, id) => query<DeliveryEstimate>("deliveryEstimate", { sheetId, id }),
    prepare: async (input) => {
      await mutate("prepareVendorAccount", input);
    },
    // The app starts every purchase at Buy, as always: nothing is opened
    // ahead of it, so nothing is signalled.
    open: async () => ({ engine: "runner" }),
    signal: async () => ({ open: false }),
    start: async (input) => attemptReadOf(await mutate("start", { ...input, ...options.start(input) })),
    active: async (accessId) => {
      const found = await query("active", { accessId });
      return found === null ? null : attemptReadOf(found);
    },
    status: async (id) => attemptReadOf(await query("status", { id })),
    cancel: async (id) => attemptReadOf(await mutate("cancel", { id })),
    answer: async (id, answer) => attemptReadOf(await mutate("answer", { id, ...answer })),
    watch(id, onStatus, onError) {
      return watchAttempt({
        id,
        poll: async () => attemptReadOf(await query("status", { id })),
        onStatus,
        onError,
        changes: options.changes,
      });
    },
  };
}

/** The seller login of an order, as My Tickets reveals it to its owner. */
export type SellerLogin = { email: string; password: string };

/**
 * My Tickets' "Show login" over the api's buy-on-behalf tRPC router: the
 * login of the seller account an access's tickets were bought with (the
 * order's, with `attemptId`), only for the buyer who owns them. It names
 * the access and the order, never a seller.
 */
export function sellerLoginRpc(options: {
  url: string;
  headers: () => Promise<Record<string, string>>;
  fetch?: typeof fetch;
}) {
  const api = createTRPCUntypedClient({
    links: [httpLink({ url: options.url, headers: options.headers, ...(options.fetch ? { fetch: options.fetch } : {}) })],
  });
  return {
    login: (accessId: string, attemptId?: string) =>
      answered(api.query("vendorLogin", { accessId, ...(attemptId ? { attemptId } : {}) }) as Promise<SellerLogin>),
  };
}
